import React, { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { SITE } from "../site.config";
import WhatsAppButton from "./WhatsAppButton";

const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Areas", href: "#coverage" },
  { label: "Quote", href: "#quote" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 w-full transition-all ${
        scrolled
          ? "bg-moffat-paper/95 backdrop-blur border-b border-moffat-line shadow-sm"
          : "bg-moffat-paper"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <a
          data-testid="navbar-logo"
          href="#top"
          className="flex items-center gap-3"
        >
          <img
            src={SITE.logoUrl}
            alt="Carpet & Lino Fitted by A.Moffat"
            className="h-11 w-11 md:h-12 md:w-12 rounded-full object-contain"
          />
          <div className="leading-none hidden sm:block">
            <p className="font-display text-[15px] md:text-[17px] text-moffat-navy">
              CARPET & LINO
            </p>
            <p className="text-[10.5px] md:text-[11px] font-bold text-moffat-red tracking-[0.18em] uppercase mt-0.5">
              Fitted by A.Moffat
            </p>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="px-3.5 py-2 text-sm font-semibold text-moffat-text hover:text-moffat-red transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="navbar-call-button"
            href={`tel:${SITE.phoneTel}`}
            className="inline-flex items-center gap-2 bg-moffat-navy hover:bg-moffat-navyDeep text-white font-bold text-sm px-5 py-2.5 rounded-sm shadow-sm transition-all hover:-translate-y-0.5"
          >
            <Phone size={14} strokeWidth={2.8} />
            CALL US TODAY
          </a>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          className="md:hidden h-10 w-10 inline-flex items-center justify-center rounded-md text-moffat-navy hover:bg-moffat-cream"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden border-t border-moffat-line bg-moffat-paper animate-float-up"
        >
          <div className="px-4 py-3 flex flex-col">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={close}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="py-3 border-b border-moffat-line/60 last:border-0 text-base font-semibold text-moffat-text"
              >
                {l.label}
              </a>
            ))}
            <div className="grid grid-cols-2 gap-2 mt-3 items-center">
              <WhatsAppButton
                variant="inline"
                testId="mobile-nav-whatsapp"
                className="w-full"
              />
              <a
                data-testid="mobile-nav-quote-cta"
                href="#quote"
                onClick={close}
                className="inline-flex items-center justify-center gap-2 bg-moffat-red text-white font-bold px-4 py-3 rounded-sm h-12"
              >
                FREE QUOTE
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
